import { useState } from "react";

import { bind as bindTuPart } from "@synthesis/shared-services/internal/application/client";
import type { BindPath } from "@synthesis/shared-services/internal/application/types";
import { uploadTuPartFile } from "@synthesis/shared-services";
import { Button, Modal, NoticeBase, setPromiseSnack } from "@synthesis/shared-ui";
import { observer } from "mobx-react-lite";

import { Uploader, type UploaderListItem } from "../../../../components/uploader";
import { useUserInfo } from "../../../../shared/hooks/use-user-info";
import { useTuParts } from "../../hooks/use-tu-parts";
import { usePageStore } from "../../application-detail-page.lib";

const SNACK_TITLES_UPLOAD = {
  pending: "Выполняется загрузка документа...",
  fulfilled: "Документ загружен",
  rejected: "Не удалось загрузить документ. Попробуйте еще раз",
} as const;

type TuPartUploadModalProps = {
  requestId: string;
  departmentId: string;
  onClose: () => void;
};

export const TuPartUploadModal = ({ requestId, departmentId, onClose }: TuPartUploadModalProps) => {
  const userInfo = useUserInfo();
  const { invalidateTuParts } = useTuParts(requestId);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleSelectFiles = (files: File[]) => {
    if (isUploading) {
      return;
    }

    setSelectedFile(files[0] ?? null);
  };

  const handleDeleteFromList = (_fileMeta: UploaderListItem) => {
    setSelectedFile(null);
  };

  const handleUpload = () => {
    if (!selectedFile || !userInfo?.id || isUploading) {
      return;
    }

    setIsUploading(true);

    const promise = (async () => {
      const fileMeta = await uploadTuPartFile({ requestId, filename: selectedFile.name, userId: userInfo.id }, selectedFile);
      const bindPath: BindPath = { requestId, departmentId, fileId: fileMeta.id };
      await bindTuPart({ path: bindPath });
      await invalidateTuParts();
      onClose();
    })();

    setPromiseSnack({ promise, promiseSettings: { title: SNACK_TITLES_UPLOAD } });

    promise
      .catch(() => undefined)
      .finally(() => {
        setIsUploading(false);
      });
  };

  return (
    <Modal.Root isOpen setIsOpen={handleOpenChange}>
      <Modal.Content className="flex max-h-[calc(100vh-4rem)] w-[700px] flex-col overflow-hidden">
        <Modal.Title>Загрузка документа</Modal.Title>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-6 p-8">
            <h3 className="heading-h4 after:text-type-error after:ml-1 after:content-['*']">Загрузите документ</h3>

            <NoticeBase
              status="info"
              titleAppearance="common"
              title="Допустимый размер файла — 500Мб"
              description="Файлы размером более 500Мб загрузить не получится."
            />

            <Uploader multiple={false} isViewFileList allowDeleteFromList onSelectFiles={handleSelectFiles} onDeleteFromList={handleDeleteFromList} />
          </div>
        </div>

        <div className="flex shrink-0 gap-4 px-8 pb-6 pt-2">
          <Button type="button" theme="primary" size="lg" disabled={!selectedFile || isUploading} onClick={handleUpload}>
            Загрузить
          </Button>
          <Button type="button" theme="secondary" size="lg" disabled={isUploading} onClick={() => handleOpenChange(false)}>
            Отмена
          </Button>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};

export const TuPartUploadModalWrapper = observer(() => {
  const store = usePageStore();
  const popupAction = store?.popupAction;

  if (!popupAction || popupAction.type !== "upload-tu-part") {
    return null;
  }

  const handleClose = () => {
    store?.setPopupAction(null);
  };

  return <TuPartUploadModal requestId={popupAction.requestId} departmentId={popupAction.departmentId} onClose={handleClose} />;
});
